import { Button } from '@helix-ds/react'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useScrollDepth } from '../../../hooks/useScrollDepth'
import { track } from '../../../utils/analytics'
import type { ConsentFlowSteps } from '../../../utils/consentFlowHelper'
import { HEALTH_RECORDS_ACCESS_ITEM_ID } from '../../hrc/healthRecordsConsent'
import {
  ConsentFlowCard,
  OPTIONAL_INTRO_IMAGE,
  PrimaryAction,
  RESEARCH_PARTICIPATION_IMAGE,
  StepPill,
  WorkflowProgress,
} from './ConsentFlowLayout'
import { getSecondaryTitle } from './consentOptionsUtils'
import {
  SecondaryChoiceLabel,
  StandingSelectionNotice,
  SubjectChoiceRows,
  type SubjectChoiceRow,
} from './SubjectChoiceRows'
import type { ConsentConfigItem } from './types'

const HEALTH_RECORDS_SECTION_KEYS = ['includedHeading', 'useHeading', 'protectionHeading', 'durationHeading']
/** The consent config version whose Health Records Access text these headings were written for. */
const HEALTH_RECORDS_HEADINGS_CONFIG_VERSION_ID = '2026-06-17'

/** Present the configured legal text verbatim; these headings do not replace its terms. */
function HealthRecordsDescription({
  item,
  configVersionId,
  trackScroll,
}: {
  item: ConsentConfigItem
  configVersionId?: string | null
  /** Same `secondary_consent_text_*` scroll-depth tracking as the other items' description. */
  trackScroll: (el: HTMLElement | null) => void
}) {
  const paragraphs = item.descriptionParagraphs?.length ? item.descriptionParagraphs : [item.description || item.title]
  // The headings are matched to paragraphs by position, so they only fit the text they were
  // written for: that config version, with an intro plus one paragraph per section. A revised
  // text could keep the paragraph count and still move a section, so anything else shows the
  // text alone rather than risk a heading above the wrong legal terms.
  const withHeadings =
    configVersionId === HEALTH_RECORDS_HEADINGS_CONFIG_VERSION_ID &&
    paragraphs.length === HEALTH_RECORDS_SECTION_KEYS.length + 1
  return (
    <div ref={trackScroll} className="space-y-5">
      {paragraphs.map((paragraph, index) => (
        <div key={index} className="space-y-2">
          {withHeadings && index > 0 ? (
            <h2 className="text-text-heading text-base font-semibold">
              <FormattedMessage id={`healthRecords.consent.${HEALTH_RECORDS_SECTION_KEYS[index - 1]}`} />
            </h2>
          ) : null}
          <p className="text-text-body text-base leading-7">{paragraph}</p>
        </div>
      ))}
    </div>
  )
}

export function SecondaryIntroScreen({
  hasItems,
  workflowSteps,
}: {
  hasItems: boolean
  workflowSteps: ConsentFlowSteps
}) {
  const intl = useIntl()
  const navigate = useNavigate()

  return (
    <ConsentFlowCard
      imageSrc={OPTIONAL_INTRO_IMAGE}
      progress={<WorkflowProgress current={[workflowSteps.secondary]} next={[workflowSteps.profile]} />}
    >
      <div className="space-y-6">
        <StepPill>{intl.formatMessage({ id: workflowSteps.secondary.labelKey })}</StepPill>
        <h1 className="text-text-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          <FormattedMessage
            id="consentOptions.secondaryIntro.heading"
            defaultMessage="A few choices that could help your family, and others - now and in the future."
          />
        </h1>
        <p className="text-text-body text-base leading-7">
          <FormattedMessage
            id="consentOptions.secondaryIntro.body1"
            defaultMessage="The choices on the following screens will not affect your child's care. But they could help advance medical research."
          />
        </p>
        <p className="text-text-body text-base leading-7">
          <FormattedMessage
            id="consentOptions.secondaryIntro.body2"
            defaultMessage="We'll walk you through four questions so you can choose how Inflection cares for your family's information. You'll decide whether to contribute to internal research that improves our testing, whether to allow sample retention for future use, whether to share anonymized data with trusted research partners, and whether to connect your health records to help us better interpret your results."
          />
        </p>
        <div className="border-border-base bg-bg-secondary rounded-xl border px-4 py-3">
          <p className="text-brand-700 text-sm leading-6 font-medium">
            <FormattedMessage
              id="consentOptions.secondaryIntro.infoBox"
              defaultMessage="Every choice is yours and you can change your answers at any time by visiting your profile."
            />
          </p>
        </div>
        <p className="text-text-subtle text-sm leading-6">
          <FormattedMessage
            id="consentOptions.secondaryIntro.hint"
            defaultMessage="Next, you'll see each option with a plain-language explanation and a chance to Accept or Decline."
          />
        </p>
        <p className="text-text-subtle text-sm leading-6">
          <FormattedMessage
            id="consentOptions.secondaryIntro.repScope"
            defaultMessage={
              'For the purposes of this consent, "you", "me", "my", and "I" can also mean your child ' +
              '(including unborn child) or the individual you are the personal representative for.'
            }
          />
        </p>
        <PrimaryAction onClick={() => navigate(hasItems ? '/consents/secondary/2' : '/profile')}>
          <FormattedMessage id="consentOptions.cta.continue" defaultMessage="Continue" />
        </PrimaryAction>
      </div>
    </ConsentFlowCard>
  )
}

export function SecondaryChoiceScreen({
  item,
  value,
  valueSelf,
  valueDependent,
  dualSecondary,
  dependentSubtitle,
  selfSubtitle,
  subjectRows,
  hasStandingSelfSelection,
  saving,
  onSelect,
  onSelectSelf,
  onSelectDependent,
  onContinue,
  workflowSteps,
  step,
  configVersionId,
}: {
  item: ConsentConfigItem
  /** Single-subject mode (legacy): one choice for optional secondary. */
  value?: boolean | undefined
  onSelect?: (value: boolean) => void
  /** When ``dualSecondary``, choices for signer vs dependent (item id suffix in parent state). */
  valueSelf?: boolean | undefined
  valueDependent?: boolean | undefined
  onSelectSelf?: (value: boolean) => void
  onSelectDependent?: (value: boolean) => void
  dualSecondary?: boolean
  dependentSubtitle?: string
  selfSubtitle?: string
  /** One row per subject (guardian-covered Siblings); replaces the single and dual layouts. */
  subjectRows?: SubjectChoiceRow[]
  /** The guardian's explicit person-level election already applies across their orders. */
  hasStandingSelfSelection?: boolean
  saving: boolean
  onContinue: () => void
  workflowSteps: ConsentFlowSteps
  /** Step index from the URL segment — passed to analytics payload. */
  step?: number
  /** Version of the consent config the item text came from. */
  configVersionId?: string | null
}) {
  const selfReady = dualSecondary ? valueSelf !== undefined : value !== undefined
  const depReady = dualSecondary ? valueDependent !== undefined : true
  const canContinue = subjectRows ? subjectRows.every((row) => row.value !== undefined) : selfReady && depReady

  const [isOpen, setIsOpen] = useState(false)
  const learnMoreParagraphs = item.descriptionParagraphs?.slice(1) ?? []
  const hasLearnMore = learnMoreParagraphs.length > 0
  const healthRecords = item.id === HEALTH_RECORDS_ACCESS_ITEM_ID

  const intl = useIntl()
  const trackDescScroll = useScrollDepth(`secondary_consent_text_${item.id}`)
  const trackExpandedDescScroll = useScrollDepth(`secondary_consent_expanded_text_${item.id}`)

  useEffect(() => {
    setIsOpen(false)
  }, [item.id])

  function handleDisclosureToggle(next: boolean) {
    setIsOpen(next)
    track(next ? 'consent.section_expanded' : 'consent.section_collapsed', {
      item_id: item.id,
      step,
    })
  }

  const continueAction = (
    <PrimaryAction disabled={!canContinue || saving} onClick={onContinue}>
      {saving ? (
        <FormattedMessage id="consentOptions.consentSign.saving" defaultMessage="Saving" />
      ) : (
        <FormattedMessage id="consentOptions.cta.continue" defaultMessage="Continue" />
      )}
    </PrimaryAction>
  )

  return (
    <ConsentFlowCard
      imageSrc={RESEARCH_PARTICIPATION_IMAGE}
      contentAlign={healthRecords ? 'start' : 'center'}
      progress={<WorkflowProgress current={[workflowSteps.secondary]} next={[workflowSteps.profile]} />}
    >
      <div className="space-y-8">
        <StepPill>{intl.formatMessage({ id: workflowSteps.secondary.labelKey })}</StepPill>
        <div className="space-y-5">
          <h1
            className={
              healthRecords
                ? 'text-brand-600 dark:text-brand-300 text-3xl font-semibold tracking-tight sm:text-4xl'
                : 'text-text-heading text-3xl font-semibold tracking-tight sm:text-4xl'
            }
          >
            {getSecondaryTitle(item)}
          </h1>
          {healthRecords ? (
            <HealthRecordsDescription item={item} configVersionId={configVersionId} trackScroll={trackDescScroll} />
          ) : (
            <div className="space-y-4">
              <div ref={trackDescScroll} className="max-h-56 overflow-y-auto pr-2">
                <p className="text-text-body text-base leading-7">
                  {item.descriptionParagraphs?.[0] ?? item.description ?? item.title}
                </p>
              </div>
              {hasLearnMore && (
                <>
                  {!isOpen ? (
                    <Button
                      type="button"
                      variant="tertiary"
                      size="sm"
                      aria-expanded="false"
                      onClick={() => handleDisclosureToggle(true)}
                    >
                      {intl.formatMessage({
                        id: 'consentOptions.secondaryChoice.learnMore',
                        defaultMessage: 'Learn more',
                      })}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div
                        ref={trackExpandedDescScroll}
                        id={`learn-more-${item.id}`}
                        role="region"
                        aria-label={`${getSecondaryTitle(item)} details`}
                        className="border-border-base bg-bg-secondary max-h-48 space-y-3 overflow-y-auto overscroll-contain rounded-lg border px-4 py-3 pr-3"
                      >
                        {learnMoreParagraphs.map((p, i) => (
                          <p key={i} className="text-text-body text-base leading-7">
                            {p}
                          </p>
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        aria-expanded="true"
                        aria-controls={`learn-more-${item.id}`}
                        onClick={() => handleDisclosureToggle(false)}
                      >
                        {intl.formatMessage({
                          id: 'consentOptions.secondaryChoice.showLess',
                          defaultMessage: 'Show less',
                        })}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {subjectRows ? (
          <SubjectChoiceRows rows={subjectRows} />
        ) : dualSecondary ? (
          <>
            <fieldset className="grid gap-3 border-0 p-0 sm:grid-cols-2">
              <legend className="text-text-heading col-span-full mb-1 text-sm font-semibold">
                {selfSubtitle ?? (
                  <FormattedMessage id="consentOptions.secondaryChoice.forYou" defaultMessage="For you" />
                )}
              </legend>
              {hasStandingSelfSelection && <StandingSelectionNotice />}
              <SecondaryChoiceLabel
                name={`${item.id}:SELF`}
                label={<FormattedMessage id="consentOptions.secondaryChoice.accept" defaultMessage="Accept" />}
                checked={valueSelf === true}
                onSelect={() => onSelectSelf?.(true)}
              />
              <SecondaryChoiceLabel
                name={`${item.id}:SELF`}
                label={<FormattedMessage id="consentOptions.secondaryChoice.decline" defaultMessage="Decline" />}
                checked={valueSelf === false}
                onSelect={() => onSelectSelf?.(false)}
              />
            </fieldset>
            <fieldset className="grid gap-3 border-0 p-0 sm:grid-cols-2">
              <legend className="text-text-heading col-span-full mb-1 text-sm font-semibold">
                {dependentSubtitle ?? (
                  <FormattedMessage id="consentOptions.secondaryChoice.forYourChild" defaultMessage="For your child" />
                )}
              </legend>
              <SecondaryChoiceLabel
                name={`${item.id}:DEPENDENT`}
                label={<FormattedMessage id="consentOptions.secondaryChoice.accept" defaultMessage="Accept" />}
                checked={valueDependent === true}
                onSelect={() => onSelectDependent?.(true)}
              />
              <SecondaryChoiceLabel
                name={`${item.id}:DEPENDENT`}
                label={<FormattedMessage id="consentOptions.secondaryChoice.decline" defaultMessage="Decline" />}
                checked={valueDependent === false}
                onSelect={() => onSelectDependent?.(false)}
              />
            </fieldset>
          </>
        ) : (
          <fieldset className="grid gap-3 border-0 p-0 sm:grid-cols-2">
            <legend className="sr-only">
              <FormattedMessage
                id="consentOptions.secondaryChoice.srLegend"
                defaultMessage="Accept or decline {title}"
                values={{ title: getSecondaryTitle(item) }}
              />
            </legend>
            <SecondaryChoiceLabel
              name={item.id}
              label={<FormattedMessage id="consentOptions.secondaryChoice.accept" defaultMessage="Accept" />}
              checked={value === true}
              onSelect={() => onSelect?.(true)}
            />
            <SecondaryChoiceLabel
              name={item.id}
              label={<FormattedMessage id="consentOptions.secondaryChoice.decline" defaultMessage="Decline" />}
              checked={value === false}
              onSelect={() => onSelect?.(false)}
            />
          </fieldset>
        )}
        <p className="text-text-subtle text-sm leading-6">
          {healthRecords ? (
            <FormattedMessage id="healthRecords.consent.reassurance" />
          ) : (
            <FormattedMessage
              id="consentOptions.secondaryChoice.hint"
              defaultMessage="This doesn't affect your child's care. You can change this at any time from your profile."
            />
          )}
        </p>
        {healthRecords ? <div className="flex justify-end">{continueAction}</div> : continueAction}
      </div>
    </ConsentFlowCard>
  )
}
